import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import type { UseFormReturn } from 'react-hook-form';

import { marketplaceApi } from '../api/marketplaceApi';
import type { BannerRootData } from '../schemas/bannerSchema';
import type {
  BannerListParams,
  BannerOrderItem,
  BannerOrderParams,
  BannerOrderUpdatePayload,
} from '../types/bannerManagement';
import { buildBannerImageUploadItems } from '../utils/buildBannerImageUploadItems';
import { buildBannerPayload } from '../utils/buildBannerPayload';
import { useBannerImageUpload } from './useBannerImageUpload';

interface CreateBannerResult {
  bannerId: number;
}

interface SaveBannerInput {
  formData: BannerRootData;
}

interface RunBannerImageUploadsParams {
  formData: BannerRootData;
  bannerId: number;
  bannerFormMethods: UseFormReturn<BannerRootData>;
  uploadBannerImages: ReturnType<
    typeof useBannerImageUpload
  >['uploadBannerImages'];
}

async function runBannerImageUploads({
  formData,
  bannerId,
  bannerFormMethods,
  uploadBannerImages,
}: RunBannerImageUploadsParams): Promise<void> {
  const uploadItems = buildBannerImageUploadItems(formData);

  if (uploadItems.length === 0) return;

  const { failedUploads } = await uploadBannerImages({
    bannerId,
    images: uploadItems,
  });

  if (failedUploads.length === 0) return;

  failedUploads.forEach(({ item }) => {
    message.error(`Erro ao enviar ${item.label}.`);

    bannerFormMethods.setValue(
      item.formFieldPath as any,
      { preview: '', file: undefined },
      { shouldDirty: true, shouldValidate: true }
    );
  });
}

export function useBannerList(params: BannerListParams) {
  const {
    data: response,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['marketplace-banners', params],
    queryFn: () => marketplaceApi.fetchBanners(params),
    refetchOnWindowFocus: false,
  });

  return {
    banners: response?.data ?? [],
    total: response?.total ?? 0,
    isLoading,
    refetch,
  };
}

export function useBannerItem(bannerId?: number) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['marketplace-banner', bannerId],
    queryFn: () => marketplaceApi.fetchBannerById(bannerId as number),
    enabled: !!bannerId,
    refetchOnWindowFocus: false,
  });

  return {
    banner: data,
    isLoading,
    refetch,
  };
}

export function useBannerCreate(
  bannerFormMethods: UseFormReturn<BannerRootData>
) {
  const queryClient = useQueryClient();
  const { uploadBannerImages, isUploading } = useBannerImageUpload();

  const { mutateAsync: createBanner, isPending } = useMutation({
    mutationFn: async ({
      formData,
    }: SaveBannerInput): Promise<CreateBannerResult> => {
      const response = await marketplaceApi.createBanner(
        buildBannerPayload(formData)
      );

      await runBannerImageUploads({
        formData,
        bannerId: response.id,
        bannerFormMethods,
        uploadBannerImages,
      });

      return {
        bannerId: response.id,
      };
    },
    onSuccess: ({ bannerId }) => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-banners'] });
      queryClient.invalidateQueries({
        queryKey: ['marketplace-banner', bannerId],
      });
    },
  });

  return {
    createBanner,
    isCreatingBanner: isPending || isUploading,
  };
}

export function useBannerUpdate(
  bannerId: number,
  bannerFormMethods: UseFormReturn<BannerRootData>
) {
  const queryClient = useQueryClient();
  const { uploadBannerImages, isUploading } = useBannerImageUpload();

  const { mutateAsync: updateBanner, isPending } = useMutation({
    mutationFn: async ({ formData }: SaveBannerInput): Promise<void> => {
      await marketplaceApi.updateBanner(bannerId, buildBannerPayload(formData));

      await runBannerImageUploads({
        formData,
        bannerId,
        bannerFormMethods,
        uploadBannerImages,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-banners'] });
      queryClient.invalidateQueries({
        queryKey: ['marketplace-banner', bannerId],
      });
    },
  });

  return {
    updateBanner,
    isUpdatingBanner: isPending || isUploading,
  };
}

export function useBannerActivate() {
  const queryClient = useQueryClient();

  const { mutateAsync: activateBanner, isPending } = useMutation({
    mutationFn: async (bannerId: number): Promise<void> => {
      await marketplaceApi.activateBanner(bannerId);
    },
    onSuccess: (_, bannerId) => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-banners'] });
      queryClient.invalidateQueries({
        queryKey: ['marketplace-banner', bannerId],
      });
    },
  });

  return {
    activateBanner,
    isActivatingBanner: isPending,
  };
}

export function useBannerDeactivate() {
  const queryClient = useQueryClient();

  const { mutateAsync: deactivateBanner, isPending } = useMutation({
    mutationFn: async (bannerId: number): Promise<void> => {
      await marketplaceApi.deactivateBanner(bannerId);
    },
    onSuccess: (_, bannerId) => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-banners'] });
      queryClient.invalidateQueries({
        queryKey: ['marketplace-banner', bannerId],
      });
    },
  });

  return {
    deactivateBanner,
    isDeactivatingBanner: isPending,
  };
}

export function useBannerDelete() {
  const queryClient = useQueryClient();

  const { mutateAsync: deleteBanner, isPending } = useMutation({
    mutationFn: async (bannerId: number): Promise<void> => {
      await marketplaceApi.deleteBanner(bannerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-banners'] });
    },
  });

  return {
    deleteBanner,
    isDeletingBanner: isPending,
  };
}

const EMPTY_BANNER_ORDER: BannerOrderItem[] = [];

export function useBannerOrder(params: BannerOrderParams | null) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['marketplace-banner-order', params],
    queryFn: () => marketplaceApi.fetchBannerOrder(params as BannerOrderParams),
    enabled: !!params,
    refetchOnWindowFocus: false,
  });

  return {
    orderedBanners: data ?? EMPTY_BANNER_ORDER,
    isLoading,
    refetch,
  };
}

export function useBannerUpdateOrder() {
  const queryClient = useQueryClient();

  const { mutateAsync: updateBannerOrder, isPending } = useMutation({
    mutationFn: async (payload: BannerOrderUpdatePayload): Promise<void> => {
      await marketplaceApi.updateBannerOrder(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-banner-order'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-banners'] });
    },
  });

  return {
    updateBannerOrder,
    isUpdatingBannerOrder: isPending,
  };
}
